// ============================================================
// notice.ts — お知らせ表示・既読管理
// ------------------------------------------------------------
// Pleasanter SiteId=660977 のお知らせを取得し、
//  - ログイン画面 : ログインフォーム下部の一覧領域に展開
//  - ログイン後   : ナビバーの通知ベルからモーダル表示 + 未読バッジ
// 既読状態はユーザ毎に localStorage で管理する。
// ============================================================

// 共通グローバル: $ (jQuery), dayjs, bootstrap は global.d.ts で宣言済み

interface Notice {
    NoticeId: number;
    Title: string;
    Body: string;
    CreatedTime: string;
}

interface NoticeListResponse {
    Result: boolean;
    Message?: string;
    NoticeList: Notice[];
}

const READ_STORAGE_PREFIX = 'nc-notice-read::';

function _escapeHtml(value: string | null | undefined): string
{
    if (value == null) {
        return '';
    }
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function _formatCreatedTime(value: string): string
{
    if (!value) {
        return '';
    }
    // サーバ側で JST (+9h) 補正済みの値をオフセットなしで返すため、
    // クライアントでの再変換は行わずそのまま整形して表示する。
    if (typeof dayjs !== 'undefined') {
        const d = dayjs(value);
        if (d && d.isValid && d.isValid()) {
            return d.format('YYYY-MM-DD HH:mm');
        }
    }
    const d = new Date(value);
    if (isNaN(d.getTime())) {
        return value;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _readStorageKey(userKey: string): string
{
    const safeKey = (userKey || 'guest').replace(/[^A-Za-z0-9_\-]/g, '_');
    return READ_STORAGE_PREFIX + safeKey;
}

function _loadReadIds(userKey: string): Set<number>
{
    try {
        const raw = window.localStorage.getItem(_readStorageKey(userKey));
        if (!raw) {
            return new Set<number>();
        }
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
            return new Set<number>(arr.filter(x => typeof x === 'number'));
        }
    } catch (e) {
        // localStorage 不可 / JSON パース失敗 → 空セット
    }
    return new Set<number>();
}

function _saveReadIds(userKey: string, ids: Set<number>): void
{
    try {
        window.localStorage.setItem(
            _readStorageKey(userKey),
            JSON.stringify(Array.from(ids)));
    } catch (e) {
        // 容量超過等 — 既読状態保存に失敗しても画面表示は継続
    }
}

interface FetchOptions {
    maxCount?: number;
    withinDays?: number;
}

function _fetchNotices(opts?: FetchOptions): JQuery.jqXHR<NoticeListResponse>
{
    const data: { [k: string]: number } = {};
    if (opts && typeof opts.maxCount === 'number' && opts.maxCount > 0) {
        data.maxCount = opts.maxCount;
    }
    if (opts && typeof opts.withinDays === 'number' && opts.withinDays > 0) {
        data.withinDays = opts.withinDays;
    }
    return $.ajax({
        url: '/api/notice/getlist',
        type: 'get',
        dataType: 'json',
        cache: false,
        data: data,
    });
}

// ログイン画面では既読管理ができないため、直近 10 件かつ 3 ヶ月 (90 日) 以内に絞る
const LOGIN_NOTICE_MAX_COUNT = 10;
const LOGIN_NOTICE_WITHIN_DAYS = 90;

function _renderNoticeItems(notices: Notice[], readIds: Set<number>, allowMarkRead: boolean): string
{
    if (!notices.length) {
        return '<div class="text-body-secondary text-center py-3">お知らせはありません。</div>';
    }
    return notices.map(n => {
        const isRead = readIds.has(n.NoticeId);
        const badge = isRead
            ? ''
            : '<span class="badge text-bg-danger ms-2 nc-notice-unread-badge">未読</span>';
        return `
<div class="nc-notice-item border rounded p-2 mb-2 ${isRead ? 'nc-notice-read' : 'nc-notice-unread'}"
     data-notice-id="${n.NoticeId}">
    <div class="d-flex align-items-center mb-1">
        <strong class="nc-notice-title">${_escapeHtml(n.Title)}</strong>
        ${badge}
        <small class="ms-auto text-body-secondary">${_escapeHtml(_formatCreatedTime(n.CreatedTime))}</small>
    </div>
    <div class="nc-notice-body">${_escapeHtml(n.Body)}</div>
    ${allowMarkRead && !isRead
        ? '<div class="text-end mt-1"><button type="button" class="btn btn-sm btn-secondary nc-notice-mark-read">既読にする</button></div>'
        : ''}
</div>`;
    }).join('');
}

// ----------------------------------------------------------------
// ログイン画面用 : 既読管理なし、表示のみ
// ----------------------------------------------------------------
function _initLoginNoticeArea(): void
{
    const $area = $('#noticeListArea');
    if (!$area.length) {
        return;
    }
    _fetchNotices({ maxCount: LOGIN_NOTICE_MAX_COUNT, withinDays: LOGIN_NOTICE_WITHIN_DAYS })
        .done(function (res)
        {
            if (!res || !res.Result) {
                $area.html('');
                return;
            }
            // ログイン前は既読管理しない（共有端末の可能性があるため）
            const readIds = new Set<number>();
            $area.html(_renderNoticeItems(res.NoticeList || [], readIds, false));
        })
        .fail(function ()
        {
            $area.html('');
        });
}

// ----------------------------------------------------------------
// ログイン後 : ナビバーのベル + モーダル + 未読バッジ + 既読操作
// ----------------------------------------------------------------
function _initAuthenticatedNoticeUi(): void
{
    const $btn = $('#noticeButton');
    if (!$btn.length) {
        return;
    }
    const userKey = String($('body').data('nc-user-code') ?? '');
    const $list = $('#noticeModalList');
    const $badge = $('#noticeUnreadBadge');

    function refresh(): void
    {
        _fetchNotices()
            .done(function (res)
            {
                if (!res || !res.Result) {
                    $list.html('<div class="text-danger text-center py-3">お知らせの取得に失敗しました。</div>');
                    $badge.addClass('d-none').text('');
                    return;
                }
                const notices = res.NoticeList || [];
                const readIds = _loadReadIds(userKey);
                // 不要な既読 ID を整理（削除済みお知らせを既読履歴から除去）
                const validIds = new Set<number>(notices.map(n => n.NoticeId));
                let dirty = false;
                readIds.forEach(id => { if (!validIds.has(id)) { readIds.delete(id); dirty = true; } });
                if (dirty) {
                    _saveReadIds(userKey, readIds);
                }
                const unread = notices.filter(n => !readIds.has(n.NoticeId)).length;
                if (unread > 0) {
                    $badge.removeClass('d-none').text(String(unread));
                } else {
                    $badge.addClass('d-none').text('');
                }
                $list.html(_renderNoticeItems(notices, readIds, true));
            })
            .fail(function ()
            {
                $list.html('<div class="text-danger text-center py-3">お知らせの取得に失敗しました。</div>');
                $badge.addClass('d-none').text('');
            });
    }

    // 既読化（個別）
    $list.on('click touch', '.nc-notice-mark-read', function ()
    {
        const $item = $(this).closest('.nc-notice-item');
        const id = Number($item.data('notice-id'));
        if (!Number.isFinite(id)) {
            return;
        }
        const readIds = _loadReadIds(userKey);
        readIds.add(id);
        _saveReadIds(userKey, readIds);
        $item.removeClass('nc-notice-unread').addClass('nc-notice-read');
        $item.find('.nc-notice-unread-badge').remove();
        $(this).remove();
        const remaining = $list.find('.nc-notice-item.nc-notice-unread').length;
        if (remaining > 0) {
            $badge.removeClass('d-none').text(String(remaining));
        } else {
            $badge.addClass('d-none').text('');
        }
    });

    // 全件既読化
    $('#noticeMarkAllReadButton').on('click touch', function ()
    {
        const readIds = _loadReadIds(userKey);
        $list.find('.nc-notice-item').each(function ()
        {
            const id = Number($(this).data('notice-id'));
            if (Number.isFinite(id)) {
                readIds.add(id);
            }
        });
        _saveReadIds(userKey, readIds);
        $list.find('.nc-notice-item').removeClass('nc-notice-unread').addClass('nc-notice-read');
        $list.find('.nc-notice-unread-badge').remove();
        $list.find('.nc-notice-mark-read').remove();
        $badge.addClass('d-none').text('');
    });

    refresh();
}

$(function ()
{
    _initLoginNoticeArea();
    _initAuthenticatedNoticeUi();
});
